/**
 * Canonical cart-drawer actions layered onto Dawn's cart infrastructure.
 * Uses Shopify's locale-aware Ajax Cart endpoints and bundled section rendering.
 */
(function () {

  function getSubmitButton(control) {
    return control.querySelector('button[type="submit"]');
  }

  function setLoading(control, loading) {
    const button = getSubmitButton(control);
    const spinner = control.querySelector('.loading__spinner');
    if (button) {
      button.disabled = loading;
      button.setAttribute('aria-disabled', String(loading));
    }
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  async function cartRequest(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || window.cartStrings.error);
    }
    return data;
  }

  async function publishCartUpdate(data, initiator, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-custom',
        initiator,
        authoritativeSections: ['cart-drawer', 'cart-icon-bubble'],
        cartData: data,
        variantId,
      });
      return true;
    }
    return false;
  }

  function normalizeBrand(value) {
    return String(value || '').trim().toLocaleLowerCase();
  }

  function validateResolvedPageBrand(drawer) {
    if (!drawer) return true;
    const bodyBrand = normalizeBrand(document.body?.dataset.brand);
    const pageBrand = normalizeBrand(drawer.dataset.pageBrand);
    const agrees = !bodyBrand || !pageBrand || bodyBrand === pageBrand;

    if (agrees) {
      delete drawer.dataset.brandResolverMismatch;
      return true;
    }

    drawer.dataset.brandResolverMismatch = 'true';
    drawer.dispatchEvent(
      new CustomEvent('cart:brand-resolver-mismatch', {
        bubbles: true,
        detail: {bodyBrand, pageBrand},
      })
    );
    console.error(
      `Cart drawer page brand "${pageBrand}" does not match body brand "${bodyBrand}".`
    );
    return false;
  }

  function syncServerDiagnostics(drawer, sectionHtml) {
    if (!drawer || !sectionHtml) return;
    const renderedDrawer = new DOMParser()
      .parseFromString(sectionHtml, 'text/html')
      .querySelector('cart-drawer');
    if (!renderedDrawer) return;

    ['cartContainsMoree', 'pageBrand', 'upsellWorld'].forEach((key) => {
      const value = renderedDrawer.dataset[key];
      if (typeof value === 'string') drawer.dataset[key] = value;
      else delete drawer.dataset[key];
    });
  }

  function reconcileServerDiagnostics(cartData) {
    const drawer = document.querySelector('cart-drawer');
    const sectionHtml = cartData?.sections?.['cart-drawer'];
    if (!drawer || !sectionHtml) return;
    syncServerDiagnostics(drawer, sectionHtml);
    validateResolvedPageBrand(drawer);
  }

  function focusInsideRefreshedDrawer(primarySelector, drawer = document.querySelector('cart-drawer')) {
    if (!drawer) return;
    let timer;
    timer = window.setTimeout(() => {
      drawer._cartFocusTimers?.delete(timer);
      const refreshedDrawer = document.querySelector('cart-drawer');
      if (
        refreshedDrawer !== drawer ||
        !drawer.isConnected ||
        !drawer.classList.contains('active') ||
        drawer._focusTrapActive === false
      ) {
        return;
      }
      // Refreshes replace the trapped nodes. Rebind the container without
      // focusing its first control; the requested status/input receives focus
      // below after the replacement is connected.
      refreshedDrawer.syncFocusTrap?.({focus: false});

      const primaryTarget = refreshedDrawer.querySelector(primarySelector);
      const heading = refreshedDrawer.querySelector(
        '[data-cart-drawer-focus-fallback], .cart-drawer__heading'
      );
      const target = primaryTarget || heading || refreshedDrawer.querySelector('.drawer__inner');
      if (!target) return;

      if (!target.matches('button, a, input, select, textarea, [tabindex]')) {
        target.setAttribute('tabindex', '-1');
      }
      if (!target.isConnected || !drawer.contains(target)) return;
      target.focus({preventScroll: true});
    }, 0);
    drawer.registerDeferredCartFocus?.(timer);
  }

  function focusAfterUpsellRefresh(drawer) {
    if (!drawer) return;
    focusInsideRefreshedDrawer(
      'cart-upsell [data-cart-upsell-add], cart-upsell button[type="submit"]',
      drawer
    );
  }

  function focusAfterDiscountRefresh(drawer) {
    focusInsideRefreshedDrawer('cart-discount-form input[name="discount"]', drawer);
  }

  async function renderCart(data, initiator, variantId) {
    const drawer = document.querySelector('cart-drawer');
    if (!data.sections || !data.sections['cart-drawer']) {
      throw new Error(window.cartStrings.error);
    }
    const published = await publishCartUpdate(data, initiator, variantId);
    if (drawer) {
      if (!published) syncServerDiagnostics(drawer, data.sections['cart-drawer']);
      drawer.renderContents(data);
      drawer.syncFocusTrap?.({focus: false});
      validateResolvedPageBrand(drawer);
    }
    return drawer;
  }

  function announce(message) {
    const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
    if (!liveRegion) return;
    liveRegion.textContent = message;
    liveRegion.setAttribute('aria-hidden', 'false');
  }

  const pendingProductForms = new WeakSet();
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (
      event.defaultPrevented ||
      !form.matches?.('form[data-type="add-to-cart-form"]') ||
      form.closest('product-form, [data-moree-us-product]')
    ) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer?.renderContents) return;
    event.preventDefault();
    if (pendingProductForms.has(form) || !form.reportValidity()) return;
    pendingProductForms.add(form);
    const button = event.submitter || getSubmitButton(form);
    const wasDisabled = button?.disabled;
    if (button) button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    let error = form.querySelector('[data-product-cart-error]');
    if (error) error.hidden = true;
    drawer.setActiveElement(button);
    let added = false;
    let serverMessage;
    try {
      const body = new FormData(form);
      body.set('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
      body.set('sections_url', window.location.pathname);
      const response = await fetch(routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin', headers: {Accept: 'application/json'}, body,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        serverMessage = typeof data.description === 'string' ? data.description : null;
        throw new Error('Add rejected');
      }
      added = true;
      drawer.classList.remove('is-empty');
      await renderCart(data, 'native-product-form', body.get('id'));
    } catch (failure) {
      if (added) {
        window.location.assign(routes.cart_url);
        return;
      }
      if (!error) {
        error = document.createElement('p');
        error.className = 'form__message';
        error.dataset.productCartError = '';
        error.setAttribute('role', 'alert');
        error.setAttribute('tabindex', '-1');
        form.append(error);
      }
      error.textContent = serverMessage || window.cartStrings.error;
      error.hidden = false;
      error.focus();
    } finally {
      pendingProductForms.delete(form);
      form.removeAttribute('aria-busy');
      if (button?.isConnected) button.disabled = wasDisabled;
    }
  });

  if (!customElements.get('cart-discount-form')) {
    customElements.define(
      'cart-discount-form',
      class CartDiscountForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.form?.addEventListener('submit', this.onSubmit);
        }

        disconnectedCallback() {
          this.form?.removeEventListener('submit', this.onSubmit);
        }

        onSubmit = async (event) => {
          event.preventDefault();
          const input = this.querySelector('input[name="discount"]');
          const status = this.querySelector('.cart-drawer__inline-status');
          const code = input?.value.trim();
          if (!code || !status) return;

          status.textContent = '';
          setLoading(this, true);
          try {
            const data = await cartRequest(routes.cart_update_url, {discount: code});
            const applications = [
              ...(data.cart_level_discount_applications || []),
              ...(data.discount_applications || []),
            ];
            const normalizedCode = code.toLocaleLowerCase();
            const applied = applications.some((application) => {
              const label = application.title || application.code || '';
              return label.toLocaleLowerCase() === normalizedCode;
            });

            const drawer = await renderCart(data, 'cart-discount-form');
            const refreshed = document.querySelector('cart-discount-form');
            const refreshedStatus = refreshed?.querySelector('.cart-drawer__inline-status');
            const message = applied
              ? refreshedStatus?.dataset.success
              : refreshedStatus?.dataset.error;
            if (refreshedStatus) refreshedStatus.textContent = message || '';
            announce(message || '');
            focusAfterDiscountRefresh(drawer);
          } catch (error) {
            status.textContent = error.message || status.dataset.error;
            announce(status.textContent);
            input?.focus({preventScroll: true});
          } finally {
            if (this.isConnected) setLoading(this, false);
          }
        };
      }
    );
  }

  if (!customElements.get('cart-upsell')) {
    customElements.define(
      'cart-upsell',
      class CartUpsell extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.form?.addEventListener('submit', this.onSubmit);
          validateResolvedPageBrand(this.closest('cart-drawer'));
        }

        disconnectedCallback() {
          this.form?.removeEventListener('submit', this.onSubmit);
        }

        onSubmit = async (event) => {
          event.preventDefault();
          const variantInput = this.querySelector('input[name="id"]');
          const status = this.querySelector(
            '[data-cart-upsell-status], .cart-drawer__inline-status'
          );
          if (!variantInput) return;

          setLoading(this, true);
          if (status) status.textContent = '';
          try {
            const variantId = Number(variantInput.value);
            const successMessage = this.dataset.success || '';
            const data = await cartRequest(routes.cart_add_url, {
              items: [{id: variantId, quantity: 1}],
            });
            const drawer = await renderCart(data, 'cart-upsell', variantId);
            announce(successMessage);
            focusAfterUpsellRefresh(drawer);
          } catch (error) {
            const errorMessage =
              status?.dataset.error || this.dataset.error || window.cartStrings.error;
            if (status) {
              status.textContent = errorMessage;
              status.setAttribute('tabindex', '-1');
              status.focus({preventScroll: true});
            } else {
              setLoading(this, false);
              getSubmitButton(this)?.focus({preventScroll: true});
            }
            announce(errorMessage);
          } finally {
            if (this.isConnected) setLoading(this, false);
          }
        };
      }
    );
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      reconcileServerDiagnostics(event?.cartData);
    });
  }

  validateResolvedPageBrand(document.querySelector('cart-drawer'));
})();

